import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Skeleton,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { useNavigate, useOutletContext } from "react-router-dom";
import { portalListTickets } from "../../api/clientPortal";
import { getClientPortalErrorMessage } from "./clientPortalErrorMessage";
import { getStatusAccentColor } from "../../utils/status";
import { getStatusChip } from "../../utils/utils";
import {
  formatPortalDateTimeFull,
  formatPortalRelativeTime,
  formatPortalAssigneeLabel,
  isPortalListableTicket,
  ticketDisplayTitle,
  ticketPreviewText,
} from "./clientPortalTicketListUtils";

const FILTERS = [
  { id: "open", label: "Em andamento" },
  { id: "closed", label: "Encerradas" },
  { id: "all", label: "Todas" },
];

function FilterPill({ active, label, count, onClick }) {
  const theme = useTheme();
  return (
    <Button
      onClick={onClick}
      size="small"
      variant={active ? "contained" : "outlined"}
      disableElevation
      sx={{
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 999,
        px: 2,
        py: 0.65,
        minHeight: 36,
        borderColor: active ? "transparent" : "divider",
        bgcolor: active ? "primary.main" : alpha(theme.palette.background.paper, 0.9),
        color: active ? "primary.contrastText" : "text.primary",
        "&:hover": {
          bgcolor: active ? "primary.dark" : alpha(theme.palette.primary.main, 0.06),
          borderColor: active ? "transparent" : "primary.light",
        },
      }}
    >
      {label}
      {count != null ? (
        <Box
          component="span"
          sx={{
            ml: 1,
            px: 0.75,
            py: 0.1,
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1.4,
            bgcolor: active ? alpha("#fff", 0.22) : alpha(theme.palette.text.primary, 0.08),
          }}
        >
          {count}
        </Box>
      ) : null}
    </Button>
  );
}

function TicketRowSkeleton() {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{ flex: 1 }}>
          <Skeleton width="45%" height={22} />
          <Skeleton width="80%" height={18} sx={{ mt: 1 }} />
          <Skeleton width="30%" height={14} sx={{ mt: 1.25 }} />
        </Box>
        <Skeleton width={88} height={24} sx={{ borderRadius: 999 }} />
      </Stack>
    </Paper>
  );
}

function EmptyState({ filter, q, navigate }) {
  const isOpen = filter === "open";
  const isClosed = filter === "closed";

  const title = isOpen
    ? "Nenhuma solicitação em andamento"
    : isClosed
    ? "Nenhuma solicitação encerrada"
    : "Você ainda não tem solicitações";

  const body = isOpen
    ? "Quando abrir uma solicitação, ela aparecerá aqui enquanto estiver sendo tratada."
    : isClosed
    ? "As solicitações concluídas ou encerradas ficam disponíveis nesta aba para consulta."
    : "Abra sua primeira solicitação para falar com nossa equipe.";

  return (
    <Paper
      variant="outlined"
      sx={{
        py: 5,
        px: 3,
        textAlign: "center",
        borderRadius: 2,
        borderStyle: "dashed",
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          mx: "auto",
          mb: 2,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
          color: "primary.main",
        }}
      >
        <SupportAgentOutlinedIcon />
      </Box>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: "auto", mb: 2.5 }}>
        {body}
      </Typography>
      {!isClosed && (
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`/portal-cliente/app/new${q}`)}>
          Abrir solicitação
        </Button>
      )}
    </Paper>
  );
}

function TicketCard({ ticket, q, navigate, theme }) {
  const updatedAt = ticket.updatedAt || ticket.createdAt;
  const relative = formatPortalRelativeTime(updatedAt);
  const full = formatPortalDateTimeFull(updatedAt);
  const title = ticketDisplayTitle(ticket);
  const preview = ticketPreviewText(ticket);
  const statusAccent = getStatusAccentColor(ticket.status, theme, {
    isClosed: ticket.isClosed,
  });

  return (
    <Paper
      component="button"
      type="button"
      onClick={() => navigate(`/portal-cliente/app/${ticket.id}${q}`)}
      variant="outlined"
      sx={{
        width: "100%",
        textAlign: "left",
        p: 0,
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        borderColor: "divider",
        bgcolor: "background.paper",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease, transform 0.15s ease",
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.35),
          boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.08)}`,
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack direction="row" alignItems="stretch">
        <Box sx={{ width: 4, bgcolor: statusAccent, flexShrink: 0 }} />
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ flex: 1, minWidth: 0, px: 2, py: 1.75 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ maxWidth: "100%" }}>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                #{ticket.id}
              </Typography>
              {!ticket.isOwnTicket && ticket.clientName && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={ticket.clientName}
                  sx={{ height: 22, fontSize: 11, maxWidth: 160 }}
                />
              )}
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {preview}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              Responsável: {formatPortalAssigneeLabel(ticket)}
            </Typography>
            {relative && (
              <Tooltip title={full ? `Atualizada em ${full}` : ""} placement="top">
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                  Atualizada {relative}
                </Typography>
              </Tooltip>
            )}
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {getStatusChip(ticket.status, theme)}
            <ChevronRightIcon sx={{ color: "text.disabled", fontSize: 22 }} />
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function ClientPortalTicketListPage() {
  const theme = useTheme();
  const { q, user } = useOutletContext();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("open");
  const [tickets, setTickets] = useState([]);
  const [includesLinked, setIncludesLinked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isPJ = user?.isPessoaJuridica || Number(user?.tipo) === 1;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await portalListTickets();
      const list = (data?.tickets || []).filter(isPortalListableTicket);
      setTickets(list);
      setIncludesLinked(Boolean(data?.includesLinkedTickets));
    } catch (e) {
      setError(getClientPortalErrorMessage(e, { context: "ticketList" }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(
    () => ({
      all: tickets.length,
      open: tickets.filter((t) => !t.isClosed).length,
      closed: tickets.filter((t) => t.isClosed).length,
    }),
    [tickets]
  );

  const visibleTickets = useMemo(() => {
    if (filter === "open") return tickets.filter((t) => !t.isClosed);
    if (filter === "closed") return tickets.filter((t) => t.isClosed);
    return tickets;
  }, [tickets, filter]);

  const firstName = String(user?.nome || user?.razaosocial || "")
    .trim()
    .split(/\s+/)[0];

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "flex-start" }}
        >
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8, fontWeight: 700 }}>
              Portal do cliente
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ mt: 0.25, lineHeight: 1.2 }}>
              Suas solicitações
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 520 }}>
              {isPJ
                ? "Acompanhe solicitações da empresa e de pessoas vinculadas ao seu cadastro."
                : firstName
                ? `${firstName}, acompanhe aqui o andamento das suas solicitações.`
                : "Acompanhe aqui o andamento das suas solicitações."}
            </Typography>
            {includesLinked && (
              <Chip
                size="small"
                variant="outlined"
                label="Inclui solicitações de pessoas vinculadas"
                sx={{ mt: 1.25, height: 24, fontSize: 11 }}
              />
            )}
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/portal-cliente/app/new${q}`)}
            sx={{
              alignSelf: { xs: "stretch", sm: "center" },
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 2.5,
              boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.28)}`,
            }}
          >
            Abrir solicitação
          </Button>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {FILTERS.map((f) => (
          <FilterPill
            key={f.id}
            active={filter === f.id}
            label={f.label}
            count={counts[f.id]}
            onClick={() => setFilter(f.id)}
          />
        ))}
      </Stack>

      {loading && (
        <Stack spacing={1.25}>
          {[1, 2, 3].map((i) => (
            <TicketRowSkeleton key={i} />
          ))}
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} action={<Button onClick={load}>Tentar de novo</Button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && visibleTickets.length === 0 && (
        <EmptyState filter={filter} q={q} navigate={navigate} />
      )}

      {!loading && !error && visibleTickets.length > 0 && (
        <Stack spacing={1.25}>
          {visibleTickets.map((t) => (
            <TicketCard key={t.id} ticket={t} q={q} navigate={navigate} theme={theme} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
